// src/features/health/components/medications/MedicationForm.tsx
// Add/edit medication form component

import React, { useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import {
  Plus,
  X,
  Clock,
  Bell,
  Save,
  Pill,
  User,
  Building,
  Calendar,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Switch } from '@/components/ui/Switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/Form';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';
import {
  medicationFormSchema,
  MedicationFormData,
  MedicationFrequency,
  MedicationRoute,
  frequencyLabels,
  routeLabels,
  Medication,
} from '../types/medicationTypes';
import { useCreateMedication, useUpdateMedication } from '../hooks/useMedications';

// ============================================================================
// Types
// ============================================================================

interface MedicationFormProps {
  childId: string;
  medication?: Medication;
  onSuccess?: (medication: Medication) => void;
  onCancel?: () => void;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export const MedicationForm: React.FC<MedicationFormProps> = ({
  childId,
  medication,
  onSuccess,
  onCancel,
  className,
}) => {
  const isEditing = !!medication;
  const createMutation = useCreateMedication();
  const updateMutation = useUpdateMedication();
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Form setup
  const form = useForm<MedicationFormData>({
    resolver: zodResolver(medicationFormSchema),
    defaultValues: {
      name: '',
      genericName: '',
      dosage: '',
      frequency: MedicationFrequency.ONCE_DAILY,
      route: MedicationRoute.ORAL,
      prescribingDoctor: '',
      pharmacy: '',
      rxNumber: '',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: '',
      instructions: '',
      sideEffects: '',
      isActive: true,
      reminderEnabled: false,
      reminderTimes: [],
      refillDate: '',
      refillQuantity: undefined,
      currentQuantity: undefined,
      notes: '',
    },
  });

  // Reminder times field array
  const {
    fields: reminderFields,
    append: appendReminder,
    remove: removeReminder,
  } = useFieldArray({
    control: form.control,
    name: 'reminderTimes' as any,
  });

  // Populate form with existing medication data
  useEffect(() => {
    if (medication) {
      form.reset({
        name: medication.name,
        genericName: medication.genericName || '',
        dosage: medication.dosage,
        frequency: medication.frequency,
        route: medication.route,
        prescribingDoctor: medication.prescribingDoctor || '',
        pharmacy: medication.pharmacy || '',
        rxNumber: medication.rxNumber || '',
        startDate: medication.startDate || '',
        endDate: medication.endDate || '',
        instructions: medication.instructions || '',
        sideEffects: medication.sideEffects || '',
        isActive: medication.isActive,
        reminderEnabled: medication.reminderEnabled,
        reminderTimes: medication.reminderTimes,
        refillDate: medication.refillDate || '',
        refillQuantity: medication.refillQuantity,
        currentQuantity: medication.currentQuantity,
        notes: medication.notes || '',
      });
    }
  }, [medication, form]);

  // Handle form submission
  const onSubmit = async (data: MedicationFormData) => {
    try {
      let result: Medication;

      if (isEditing) {
        result = await updateMutation.mutateAsync({
          medicationId: medication.id,
          data,
        });
      } else {
        result = await createMutation.mutateAsync({
          childId,
          data,
        });
      }

      onSuccess?.(result);
    } catch (error) {
      console.error('Failed to save medication:', error);
    }
  };

  const reminderEnabled = form.watch('reminderEnabled');

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn('space-y-6', className)}
      >
        {/* Basic Information */}
        <div className="bg-white rounded-xl border border-border-light p-6">
          <div className="flex items-center gap-2 mb-6">
            <Pill className="w-5 h-5 text-sage-600" />
            <h3 className="font-display font-semibold text-navy">
              Medication Information
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Medication Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Keppra, Baclofen" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="genericName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Generic Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Levetiracetam" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dosage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Dosage *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., 250mg, 5mL" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="frequency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Frequency *</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(frequencyLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="route"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Route *</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select route" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(routeLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border border-border-light p-3">
                  <div>
                    <FormLabel className="font-medium">Active Medication</FormLabel>
                    <FormDescription className="text-sm">
                      Currently being taken
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Prescription Details */}
        <Accordion type="single" collapsible className="space-y-4">
          <AccordionItem value="prescription" className="bg-white rounded-xl border border-border-light">
            <AccordionTrigger className="px-6 py-4">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-sage-600" />
                <span className="font-display font-semibold text-navy">
                  Prescription Details
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="grid md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="prescribingDoctor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prescribing Doctor</FormLabel>
                      <FormControl>
                        <Input placeholder="Dr. Smith" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="pharmacy"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pharmacy</FormLabel>
                      <FormControl>
                        <Input placeholder="CVS, Walgreens, etc." {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rxNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rx Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Prescription number" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormDescription>Leave blank if ongoing</FormDescription>
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Reminders */}
          <AccordionItem value="reminders" className="bg-white rounded-xl border border-border-light">
            <AccordionTrigger className="px-6 py-4">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-sage-600" />
                <span className="font-display font-semibold text-navy">
                  Reminders
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <FormField
                control={form.control}
                name="reminderEnabled"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border border-border-light p-3 mb-4">
                    <div>
                      <FormLabel className="font-medium">Enable Reminders</FormLabel>
                      <FormDescription>
                        Get notified when it's time to take this medication
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {reminderEnabled && (
                <div className="space-y-3">
                  <Label>Reminder Times</Label>
                  {reminderFields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-warm-gray" />
                      <Controller
                        control={form.control}
                        name={`reminderTimes.${index}`}
                        render={({ field }) => (
                          <Input type="time" {...field} className="w-32" />
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeReminder(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendReminder('08:00' as any)}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Time
                  </Button>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Refill Tracking */}
          <AccordionItem value="refill" className="bg-white rounded-xl border border-border-light">
            <AccordionTrigger className="px-6 py-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-sage-600" />
                <span className="font-display font-semibold text-navy">
                  Refill Tracking
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="grid md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="currentQuantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Current Quantity</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseInt(e.target.value) : undefined
                            )
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="refillQuantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Refill Quantity</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseInt(e.target.value) : undefined
                            )
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="refillDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Next Refill Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Instructions & Notes */}
          <AccordionItem value="notes" className="bg-white rounded-xl border border-border-light">
            <AccordionTrigger className="px-6 py-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-sage-600" />
                <span className="font-display font-semibold text-navy">
                  Instructions & Notes
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="instructions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Instructions</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Take with food, shake well before use, etc."
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sideEffects"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        Known Side Effects
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Drowsiness, nausea, etc."
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Additional Notes</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Any other relevant information..."
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border-light">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isPending} className="gap-2">
            <Save className="w-4 h-4" />
            {isPending
              ? 'Saving...'
              : isEditing
              ? 'Update Medication'
              : 'Add Medication'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default MedicationForm;



